import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/**
 * A confirmed press that carries a reason — a correction, a decision, a reopening, a figure set
 * by hand (the advisor record page's roadmap, K6). It is the hub's confirmed press (`team.tsx`,
 * `accounts.tsx`) with a box for the reason: an `AlertDialog` held open while the answer is out,
 * so the press cannot be repeated and the dialog cannot be closed over a write in flight.
 *
 * **Words in, never an error** (the F1 kit plan's R4): `onConfirm` resolves to `null` when the
 * write landed, which closes the dialog, or to the sentence to say — a refusal the domain has
 * already put in words — which is said inside the dialog, kept open with the reason as typed so
 * the press can be made again. The design system never sees an `ApiError`.
 *
 * **A required reason disables the press while it is blank**: a press that can only be refused is
 * not offered (Veritant's decision). **`max` is said, never
 * enforced** — survey-api is the judge of a length (CLAUDE.md's rules of the code), so a reason
 * over it is still sent, with the count said beside the box. Both judge the reason as it goes out,
 * trimmed, which is how survey-api counts it. An optional reason left blank goes out as `''`, for
 * the caller to omit.
 *
 * **Each opening starts from `initialReason`** — `''` unless the caller kept one: a correction
 * refused for a reason that had nothing to do with its reason (a 409) is saved again with the same
 * one, and retyping it is not the person's job (SR-222's F4, R6).
 *
 * **`onClosed`**, when given, is called once the dialog has gone — after the confirm closed it or
 * after Cancel or Esc — and focus is then the caller's: the dialog does not hand it back to what
 * held it on opening. A caller whose press was disabled while the dialog was up (an `InlineField`
 * in `saving`) has nowhere for it to go back to, and settles its own state only once the dialog is
 * gone, so its focus rule meets a page with nothing focused rather than a dialog on its way out.
 *
 * **A press can carry more than a reason** — a work item parked "Pending firm" carries the day to
 * follow it up on (SR-222, F9's R7). The caller draws that field as `children`, below the reason so
 * the reason stays the dialog's first box, and keeps its value; `incomplete` holds the press
 * disabled while the caller's own field is not filled, by the same rule as a blank required
 * reason. The dialog judges nothing of it. A refusal is still said under everything, where a
 * refusal of the extra field reads beside it.
 *
 * **`enterConfirms`** opens the dialog with focus in the reason box rather than on Cancel (the
 * `AlertDialog`'s own default), and makes Enter there the press — Shift+Enter a new line — for a
 * write made often and in a row, where the reason is a short note: a figure's cell is Enter to save,
 * Enter to confirm (SR-222's F5, the owner's ruling of 2026-10-04 on Q2) — and an answer's
 * correction, whose reason is optional since SR-403 (the owner's ruling of 2026-10-09). Off by
 * default, so a decision still opens on Cancel.
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  reasonLabel = 'Reason',
  required,
  max,
  confirmLabel,
  onConfirm,
  initialReason = '',
  onClosed,
  incomplete = false,
  enterConfirms = false,
  children
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  reasonLabel?: string;
  required: boolean;
  /** survey-api's bound on the reason, said beside the box. */
  max?: number;
  confirmLabel: string;
  onConfirm: (reason: string) => Promise<Nullable<string>>;
  /** The box's text on each opening. */
  initialReason?: string;
  /** Called once the dialog has gone; focus is then the caller's. */
  onClosed?: () => void;
  /** The caller's own field, under the reason, is not filled: the press is held disabled. */
  incomplete?: boolean;
  /** Focus in the reason box on opening, and Enter there confirms (Shift+Enter a new line). */
  enterConfirms?: boolean;
  /** Fields of the caller's, drawn under the reason. */
  children?: React.ReactNode;
}) {
  const [reason, setReason] = React.useState(initialReason);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [wasOpen, setWasOpen] = React.useState(open);
  const boxId = React.useId();
  const boundId = React.useId();
  const box = React.useRef<HTMLTextAreaElement>(null);

  // Each opening starts afresh, from `initialReason`: a reason belongs to the press it was typed
  // for. Set while rendering the opening, not in an effect after it, so the last reason never
  // paints for a frame.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason(initialReason);
      setRefusal(null);
    }
  }

  const trimmed = reason.trim();
  const pressable = !busy && !incomplete && !(required && trimmed === '');

  const confirm = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const said = await onConfirm(trimmed);
      if (said === null) onOpenChange(false);
      else setRefusal(said);
    } catch (error) {
      // The contract is a sentence or null; a callback that throws broke it, and whether the write
      // landed is unknown — so the dialog says that much rather than nothing, and logs the break.
      console.error('[ReasonDialog] the confirm callback threw instead of answering:', error);
      setRefusal('No answer could be read — check whether it was saved before trying again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialogContent
        onOpenAutoFocus={
          enterConfirms
            ? (event) => {
                event.preventDefault();
                box.current?.focus();
              }
            : undefined
        }
        onCloseAutoFocus={
          onClosed &&
          ((event) => {
            event.preventDefault();
            onClosed();
          })
        }
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-1 text-sm">
          <label htmlFor={boxId} className="text-muted-foreground">
            {reasonLabel}
            {!required && ' (optional)'}
          </label>
          <Textarea
            ref={box}
            id={boxId}
            value={reason}
            disabled={busy}
            rows={3}
            aria-describedby={max === undefined ? undefined : boundId}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (
                !enterConfirms ||
                event.key !== 'Enter' ||
                event.shiftKey ||
                event.nativeEvent.isComposing
              ) {
                return;
              }
              event.preventDefault();
              if (pressable) void confirm();
            }}
          />
          {max !== undefined && (
            <span id={boundId} className="text-xs text-muted-foreground">
              {trimmed.length > max
                ? `${count(trimmed.length)} typed — the limit is ${count(max)}`
                : `${count(max)} characters at most`}
            </span>
          )}
        </div>
        {children}
        {refusal && (
          <p role="alert" className="text-sm text-destructive">
            {refusal}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!pressable}
            onClick={(event) => {
              // Held open until the answer is in; `confirm` closes it on a landed write.
              event.preventDefault();
              void confirm();
            }}
          >
            {busy ? 'Saving…' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function count(n: number): string {
  return n.toLocaleString('en-US');
}
